import { ReactNode } from "react";
import ArchivedIcon from "../../assets/icons/ArchivedIcon";
import CheckCircleIcon from "../../assets/icons/CheckCircleIcon";
import DeleteIcon from "../../assets/icons/DeleteIcon";
import DismissIcon from "../../assets/icons/DismissIcon";
import TextBoxIcon from "../../assets/icons/TextBoxIcon";
import WarningIcon from "../../assets/icons/WarningIcon";
import { CustomModalProps } from "../../types";
import { cn } from "../../utils/string";
import CustomModal from "./CustomModal";

const ICON_CONFIG = {
  ARCHIVE: {
    icon: <ArchivedIcon height={28} width={28} />,
    className: "bg-theme-error-50 text-theme-error-500",
  },
  WARNING: {
    icon: <WarningIcon height={28} width={28} />,
    className: "bg-theme-yellow-50 text-theme-yellow-600",
  },
  CHECK_CIRCLE: {
    icon: <CheckCircleIcon height={28} width={28} />,
    className: "bg-theme-success-25 text-theme-success-500",
  },
  DELETE: {
    icon: <DeleteIcon height={28} width={28} />,
    className: "bg-theme-error-50 text-theme-error-500",
  },
  TEXT_BOX: {
    icon: <TextBoxIcon height={28} width={28} />,
    className: "bg-theme-success-50 text-theme-success-500",
  },
  DISMISS: {
    icon: <DismissIcon height={28} width={28} />,
    className: "bg-theme-error-50 text-theme-error-500",
  },
};

export type ConfirmModalIconType = keyof typeof ICON_CONFIG;

type ConfirmModalProps<T> = {
  data?: T;
  confirmTitle?: string | ReactNode;
  questionText?: string;
  submitDisabled?: boolean;
  okLoading?: boolean;
  handleOk: (val?: T) => void;
  children?: ReactNode;
  alertClassName?: string;
  hideAlert?: boolean;
  closable?: boolean;
  modalClassName?: string;
  handleCancel?: () => void;
  icon: ConfirmModalIconType;
} & CustomModalProps;

export default function ConfirmModal<T>({
  data,
  confirmTitle = "Delete This.",
  questionText = "Are sure to delete this item?",
  okText = "Delete",
  okLoading,
  submitDisabled,
  alertClassName = "",
  disableCloseButton,
  okButtonProps,
  handleOk,
  handleClose,
  handleCancel,
  children,
  hideAlert,
  closable = false,
  modalClassName = "footer-end",
  icon,
  ...rest
}: ConfirmModalProps<T>) {
  return (
    <CustomModal
      width={400}
      className={`confirm-modal ${modalClassName}`}
      handleClose={handleClose}
      closable={closable}
      okText={okText}
      onOk={() => handleOk(data)}
      okButtonProps={{
        loading: okLoading,
        className: "button-primary",
        ...okButtonProps,
      }}
      cancelButtonProps={{
        className: "button-white-bordered",
        onClick: handleCancel || handleClose,
      }}
      {...rest}
    >
      {!hideAlert ? (
        <div className={cn(alertClassName, "space-y-5")}>
          <div
            className={cn(
              "size-[50px] flex items-center justify-center rounded-full",
              ICON_CONFIG?.[icon]?.className,
            )}
          >
            {ICON_CONFIG?.[icon]?.icon}
          </div>
          <h4 className="text-theme-gray-900 text-xl font-medium leading-normal">
            {confirmTitle}
          </h4>
          <p className="text-theme-gray-700 text-base font-normal leading-normal">
            {questionText}
          </p>
        </div>
      ) : null}

      {children}
    </CustomModal>
  );
}
